import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import { staticURL } from 'core/url';
import { ComponentRegistry } from 'core/registry';
import { clientStore } from 'apps/client/app';
import { Heading } from 'components';
import { ProPlan } from './ProPlan';
import { TeamPlan } from './TeamPlan';
import { BusinessPlan } from './BusinessPlan';
import { FreePlan } from './FreePlan';

export const PlanName = styled(Heading)`
  text-align: center;
`;

const Subtitle = styled(Heading)`
  font-weight: 400;
  text-align: center;
  margin: 10px auto;
`;

type PlansWrapperProps = {
  className?: string;
};

export const PlansWrapper: React.FC<PlansWrapperProps> = observer(
  ({ children, className }) => {
    return (
      <div
        className={
          className ||
          `flex justify-between flex-wrap mt-8 gap-12 ${
            clientStore.isMobile ? 'flex flex-col' : ''
          }`
        }
      >
        {children}
      </div>
    );
  },
);

type PlanChooserProps = {
  selectedIndividual?: boolean;
  activePlan?: 'pro' | 'business';
  className?: string;
  teamChooseText?: string;
  businessChooseText?: string;
  shouldShowOneRow?: boolean;
  hideSwitcher?: boolean;
  isOnboardingChoosePlan?: boolean;
  onClickSwitch?: (mode: string) => void;
  onClickIndividualFree?: () => void;
  onClickIndividualForKind?: () => void;
  onClickCompanyForTeam?: () => void;
  onClickCompanyForBusiness?: () => void;
};

export const PlanChooser: React.FC<PlanChooserProps> = observer(
  ({
    className = '',
    hideSwitcher = false,
    shouldShowOneRow = false,
    activePlan,
    onClickSwitch,
    teamChooseText,
    businessChooseText,
    selectedIndividual,
    isOnboardingChoosePlan,
    onClickCompanyForTeam,
    onClickIndividualForKind,
    onClickCompanyForBusiness,
    onClickIndividualFree,
  }) => {
    const defaultTagStyle = {
      background: 'transparent',
      border: 'none',
      margin: 0,
      fontSize: 16,
    };

    const activityTagStyle = {
      background: '#000',
      color: '#fff',
      margin: 0,
      fontSize: 16,
      fontWeight: 800,
    };

    const totalPlans = [
      onClickCompanyForTeam,
      onClickIndividualForKind,
      onClickCompanyForBusiness,
      onClickIndividualFree,
    ].filter(Boolean).length;

    const planWidth = 294;
    const getWrapperInfo = () => {
      if (clientStore.windowWidth >= 1170) {
        return `flex mt-4 overflow-y-scroll justify-center items-center gap-4 mx-auto my-0  w-[${
          totalPlans * planWidth
        }px]`;
      }

      if (clientStore.windowWidth >= 784 && clientStore.windowWidth < 1170) {
        return `flex mt-4 overflow-y-scroll mx-auto my-0 flex-wrap mt-8 justify-center gap-4 w-[${
          planWidth * Math.min(2, totalPlans)
        }px]`;
      }

      if (clientStore.windowWidth <= 784) {
        return 'flex mt-4 overflow-y-scroll flex-col items-center justify-center gap-4';
      }
    };

    const orderedPlan = (
      <PlansWrapper className={getWrapperInfo()}>
        {/* {isOnboardingChoosePlan && onClickIndividualFree ? (
          <FreePlan onSelect={onClickIndividualFree} />
        ) : (
          <FreePlan
            onSelect={onClickIndividualFree}
            buttonText='Current'
            disabled
          />
        )} */}
        <TeamPlan
          isActivePlan={activePlan === 'pro'}
          teamChooseText={teamChooseText}
          onSelect={() => {
            onClickCompanyForTeam();
          }}
        />
        <BusinessPlan
          isActivePlan={activePlan === 'business'}
          businessChooseText={businessChooseText}
          onSelect={onClickCompanyForBusiness}
        />
      </PlansWrapper>
    );

    const planSwitcher = (
      <PlansWrapper className={getWrapperInfo()}>
        {/* {selectedIndividual ? (
          <>
            {onClickIndividualFree && (
              <FreePlan onSelect={onClickIndividualFree} />
            )}
          </>
        ) : (
          <>
            <FreePlan
              onSelect={onClickIndividualFree}
              buttonText='Current'
              withoutButton
              disabled
            />

            <TeamPlan
              isActivePlan={activePlan === 'pro'}
              teamChooseText={teamChooseText}
              onSelect={() => {
                onClickCompanyForTeam();
              }}
            />
            <BusinessPlan
              isActivePlan={activePlan === 'business'}
              businessChooseText={businessChooseText}
              onSelect={onClickCompanyForBusiness}
            />
          </>
        )} */}

        <TeamPlan
          isActivePlan={activePlan === 'pro'}
          teamChooseText={teamChooseText}
          onSelect={() => {
            onClickCompanyForTeam();
          }}
        />
        <BusinessPlan
          isActivePlan={activePlan === 'business'}
          businessChooseText={businessChooseText}
          onSelect={onClickCompanyForBusiness}
        />
      </PlansWrapper>
    );

    return (
      <div className={className}>
        <Heading variant='primary' style={{ textAlign: 'center' }} size='large'>
          Choose the plan that is right for you
        </Heading>
        <Subtitle size='medium'>
          Experience how Wikifactory can accelerate your product releases and
          time to market today.
        </Subtitle>

        <div className=' max-w-[1430px] my-0 mx-auto justify-center items-center overflow-y-scroll'>
          {!hideSwitcher && (
            <div className='p-1 flex justify-center border-2 rounded-full'>
              <div
                data-testid='choosePlanPage-company-button'
                className='rounded-full flex justify-center items-center cursor-pointer'
                onClick={() => onClickSwitch?.('pro')}
                style={{
                  ...(selectedIndividual ? defaultTagStyle : activityTagStyle),
                  padding: 2,
                  paddingRight: 22,
                  height: 38,
                }}
              >
                <img width={24} src={staticURL('assets/icons/company.svg')} />
                &nbsp; Company
              </div>
              <div
                data-testid='choosePlanPage-individual-button'
                className='rounded-full flex justify-center items-center cursor-pointer'
                onClick={() => onClickSwitch?.('individual')}
                style={{
                  ...(selectedIndividual ? activityTagStyle : defaultTagStyle),
                  padding: 2,
                  height: 38,
                  paddingLeft: 22,
                }}
              >
                Individual &nbsp;
                <img
                  width={24}
                  src={staticURL('assets/icons/individual.svg')}
                />
              </div>
            </div>
          )}
          {shouldShowOneRow ? orderedPlan : planSwitcher}
          <div className='w-full mx-auto my-0 flex justify-center items-center'>
            <a
              className='text-blue-500 block my-4 cursor-pointer text-12'
              target='_blank'
              href='https://wikifactory.com/pricing/#plan-breakdown'
              rel='noreferrer'
            >
              See Plan Breakdown
            </a>
          </div>
        </div>
      </div>
    );
  },
);

ComponentRegistry.add('modals.PlanChooser', PlanChooser);
